import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';

/** The four notification kinds the dashboard raises. */
export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

/** Boxicons glyph per variant, mirroring the icons in the Figma spec. */
const VARIANT_ICONS: Record<ToastVariant, string> = {
  success: 'bx-check-circle',
  error: 'bx-x-circle',
  warning: 'bx-error',
  info: 'bx-info-circle',
};

/**
 * Replaces the stock ngx-toastr toast so every notification in the app renders
 * as the frosted card from the design: accent rail, filled icon disc, title +
 * body copy, and a close button.
 *
 * Registered globally through `toastComponent` in app.config.ts, so the ~400
 * existing `toastr.success(...)` / `toastr.error(...)` call sites pick it up
 * without changes.
 */
@Component({
  selector: '[toast-component]',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fnf-toast.component.html',
  styleUrl: './fnf-toast.component.css',
  // The base class binds this trigger via @HostBinding('@flyInOut'); animations
  // are not inherited, so it has to be redeclared here.
  animations: [
    trigger('flyInOut', [
      state('inactive', style({ opacity: 0, transform: 'translateX(24px) scale(0.96)' })),
      state('active', style({ opacity: 1, transform: 'translateX(0) scale(1)' })),
      state('removed', style({ opacity: 0, transform: 'translateX(24px) scale(0.96)' })),
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
    ]),
  ],
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FnfToastComponent extends Toast {
  constructor(toastrService: ToastrService, toastPackage: ToastPackage, ngZone: NgZone) {
    super(toastrService, toastPackage, ngZone);
  }

  /** ngx-toastr reports the type as `toast-success`, `toast-error`, and so on. */
  get variant(): ToastVariant {
    const type = (this.toastPackage.toastType || '').replace(/^toast-/, '');
    return type in VARIANT_ICONS ? (type as ToastVariant) : 'info';
  }

  get iconClass(): string {
    return VARIANT_ICONS[this.variant];
  }

  /** Screen readers announce failures assertively, everything else politely. */
  get ariaLive(): 'assertive' | 'polite' {
    return this.variant === 'error' ? 'assertive' : 'polite';
  }

  /** Keeps a close click from also firing the host tap handler. */
  close(event: MouseEvent): void {
    event.stopPropagation();
    this.remove();
  }
}
